// ASO.Market client (api/cloudflare/src/routes/admin/aso-market.ts): ordering
// ratings, reviews and installs for our Play apps, the campaigns, the money.
import { api } from './client';

const BASE = '/admin/aso-market';

export type AsomEnv = 'dev' | 'prod';

export interface AsomAccount { env: AsomEnv; email: string; has_password: boolean; max_cost: number; key_configured: boolean }

export interface AsomCountry { code: string; name: string; flag: string; language: string; regions: string[] }

export interface AsomPendingTopUp {
  id: number; amount: number; created_at: string; expires_at: string | null; wallet_address: string | null; payment_url: string | null;
}

export interface AsomOverview {
  account: AsomAccount;
  can_manage: boolean;
  connected: boolean;
  error: string | null;
  profile?: { id: number; name: string; email: string; balance: number; is_banned: boolean };
  finances?: { balance: number; spent_month: number; waiting: number; pending_top_ups: AsomPendingTopUp[] };
  constraints?: { minIntervalMinutes: number; minReviewsNumber: number; maxSearchKeywordLength: number };
  countries?: AsomCountry[];
}

export interface AsomApp {
  id: string; name: string; bundle_id: string; icon_url: string | null; status: string;
  is_banned: boolean; in_store: boolean; forbidden_countries: string[];
  hold: { kind: string; until: string | null } | null;
}

export interface AsomAppsData {
  apps: AsomApp[];
  spike: { enabled: boolean; min_devices: number; hours: number };
  unrestricted: boolean;
}

export interface AsomOrderInput {
  app_id: string | null;
  package_name: string;
  type: 1 | 3;
  countries: string[];
  total: number;
  interval_hours: number;
  rating: number;
  add_text: boolean;
  review_texts: string[];
  search_keyword_type: 0 | 1 | 2;
  search_keyword: string | null;
  no_guarantee: boolean;
  require_in_app_screenshots: boolean;
  is_high_risk: boolean;
  add_likes: boolean;
  tasks_per_country_share_limit: number | null;
  promo_code: string | null;
}

export interface AsomQuote {
  price: number; percent: number; cost: number; balance: number; max_cost: number;
  over_cap: boolean; can_exceed_cap: boolean; share_min: number | null; per_hour: number; hours: number;
}

export type AsomState = 'submitting' | 'created' | 'rejected' | 'unknown' | 'lost' | 'planned' | 'held' | 'skipped';

export interface AsomCampaign {
  id: number; env: AsomEnv; remote_id: number | null; app_id: string | null; package_name: string; application_url: string;
  app_name: string | null; app_logo_url: string | null; app_banned?: number | null;
  type: number; countries: string[]; total: number; interval_hours: number; rating: number; add_text: boolean;
  search_keyword_type: number; search_keyword: string | null; options: Record<string, unknown>; review_texts_count: number;
  quoted_price: number | null; price: number | null; state: AsomState; status: number | null; message: string | null;
  completed: number; in_progress: number; in_validation: number; canceled: number; refunded: number;
  cancellation_reason: number | null; available_for_restart: boolean;
  remote_created_at: string | null; started_at: string | null; created_by: string | null;
  created_by_name: string | null; created_by_email: string | null;
  created_at: string; synced_at: string | null; finished_at: string | null;
  /** Placed by the release plan, not by a person. */
  auto: boolean; attempts?: number; next_try_at?: string | null;
}

export interface AsomCampaignDetail {
  campaign: AsomCampaign & { review_texts: string[] };
  live: {
    review_requests: number | null; canceled_by_campaign: number | null; is_high_risk: boolean; no_guarantee: boolean;
    add_likes: boolean; require_in_app_screenshots: boolean; share_limit: number | null;
  } | null;
  geo: {
    assignedCount: number; finishedCount: number; inProgressCount: number; validationCount: number;
    countries: { country: { code: string; name: string; flag: string }; assignedCount?: number; finishedCount?: number; inProgressCount?: number; validationCount?: number }[];
  } | null;
  activity: { clickData: { date: string; count: number }[]; openingData: { date: string; count: number }[] } | null;
}

export interface AsomTask {
  id: number; createdAt: string; status: number; price: number;
  review: { id: number; text: string | null; rating: number | null; postedAt: string | null; nickname: string | null } | null;
}

export interface AsomTransaction {
  id: number; amount: number; created_at: string; kind: string;
  campaign_remote_id: number | null; campaign_id: number | null; app_name: string | null;
}

export interface AsomInvoice {
  success: boolean; payment_id: string; payment_url: string; amount: number; currency: string;
  network_id: string; wallet_address: string; expires_at: string;
}

export const getOverview = () => api.get<AsomOverview>(`${BASE}/overview`);
export const saveAccount = (body: { env: AsomEnv; email: string; password?: string; max_cost: number }) =>
  api.put<{ balance: number; name: string }>(`${BASE}/account`, body);
export const getApps = () => api.get<AsomAppsData>(`${BASE}/apps`);
export const quoteOrder = (o: AsomOrderInput) => api.post<AsomQuote>(`${BASE}/quote`, o);
export const placeOrder = (o: AsomOrderInput) =>
  api.post<{ id: number; remote_id?: number; state: AsomState; status?: number | null; message: string | null }>(`${BASE}/orders`, o);
export const listCampaigns = (params: { status?: string; origin?: string; q?: string; app_id?: string }) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString();
  return api.get<{ env: AsomEnv; campaigns: AsomCampaign[]; synced_at: string | null }>(`${BASE}/campaigns${qs ? `?${qs}` : ''}`);
};
export const syncCampaigns = () => api.post<{ seen: number; changed: number; resolved: number }>(`${BASE}/sync`);
export const getCampaign = (id: number) => api.get<AsomCampaignDetail>(`${BASE}/campaigns/${id}`);
export const getCampaignTasks = (id: number, offset = 0) => api.get<{ total: number; items: AsomTask[] }>(`${BASE}/campaigns/${id}/tasks?offset=${offset}`);
export const cancelCampaign = (id: number) => api.post<{ status: number | null }>(`${BASE}/campaigns/${id}/cancel`);
export const restartCampaign = (id: number) => api.post<{ status: number | null }>(`${BASE}/campaigns/${id}/restart`);
export const getHistory = (offset = 0) => api.get<{ total: number; items: AsomTransaction[] }>(`${BASE}/finances/history?offset=${offset}`);
export const topUp = (amount: number) => api.post<AsomInvoice>(`${BASE}/finances/top-up`, { amount });
export const checkRank = (package_name: string, country: string, keyword: string) =>
  api.post<{ rank: number | null }>(`${BASE}/rank`, { package_name, country, keyword });
// ---- Auto orders after release ----

export interface AsomPlan {
  countries: string[];
  install_keyword: string | null;
  review_keywords: string[];
  /** Per country; null = Settings, 0 = none. */
  installs: number | null;
  reviews: number | null;
}

export type AsomPlanState = 'waiting' | 'launched' | null;

export interface AsomAutoSettings {
  enabled: boolean; installs: number; installIntervalMin: number; reviews: number; reviewIntervalMin: number; reviewRating: number;
}

export interface AsomAppPlan {
  app_id: string; name: string; bundle_id: string; released_at: string | null;
  state: AsomPlanState; launched_at: string | null; plan: AsomPlan;
}

export interface AsomAutoApp {
  id: string; name: string; bundle_id: string; icon_url: string | null; status: string; is_banned: boolean;
  released_at: string | null; state: AsomPlanState; launched_at: string | null; plan: AsomPlan;
  /** Auto orders by bucket: planned, held, submitting, running, finished, cancelled, rejected, skipped… */
  orders: Record<string, number>;
}

export const getAutoSettings = () => api.get<{ settings: AsomAutoSettings; can_manage: boolean }>(`${BASE}/auto/settings`);
export const saveAutoSettings = (s: Partial<AsomAutoSettings>) => api.put<{ settings: AsomAutoSettings }>(`${BASE}/auto/settings`, s);
export const listAutoApps = () => api.get<{ apps: AsomAutoApp[] }>(`${BASE}/auto/apps`);
export const getAppPlan = (appId: string) => api.get<AsomAppPlan>(`${BASE}/apps/${appId}/plan`);
export const saveAppPlan = (appId: string, plan: AsomPlan) => api.put<AsomAppPlan>(`${BASE}/apps/${appId}/plan`, plan);
export const launchAppPlan = (appId: string) => api.post<{ queued: number }>(`${BASE}/apps/${appId}/plan/launch`);
export const decideAutoOrder = (id: number, action: 'approve' | 'skip') =>
  api.post<{ outcome: string }>(`${BASE}/campaigns/${id}/${action}`);

export const generateReviewTexts = (body: { app_id: string | null; package_name: string; count: number; rating: number; language: string; notes: string }) =>
  api.post<{ texts: string[] }>(`${BASE}/review-texts`, body);
